import React, { useMemo } from "react";
import { MOTION } from "./motionTokens";
import {
  buildParticles,
  prefersReducedMotion,
  DEFAULT_VARIANT,
} from "./particleField";

/**
 * ParticleBurst — one radial spray of light, centred on its parent.
 *
 * Purely decorative: `pointer-events: none` and aria-hidden throughout, so it
 * can be dropped over any celebration without stealing taps or polluting the
 * accessibility tree. The parent owns positioning (this fills it) and mounting
 * (unmount to stop; there is no internal timer).
 *
 * Degrades to nothing at all under prefers-reduced-motion — a burst that merely
 * ran faster would still be the thing the setting is asking us not to do.
 */
const ParticleBurst = ({
  count = 24,
  variant = DEFAULT_VARIANT,
  seed = 1,
  durationMs = MOTION.particleBurstMs,
  spreadDeg = 360,
  startAngleDeg = 0,
  className,
  style,
}) => {
  const reduced = prefersReducedMotion();

  const particles = useMemo(
    () => buildParticles({ count, variant, seed, spreadDeg, startAngleDeg }),
    [count, variant, seed, spreadDeg, startAngleDeg]
  );

  if (reduced || particles.length === 0) return null;

  const isStreak = variant === "streak";

  return (
    <div
      data-testid="particle-burst"
      data-variant={variant}
      data-particle-count={particles.length}
      className={className}
      aria-hidden="true"
      style={{
        position: "absolute",
        inset: 0,
        pointerEvents: "none",
        overflow: "visible",
        ...style,
      }}
    >
      {particles.map((p) => (
        <span
          key={p.id}
          style={{
            position: "absolute",
            left: "50%",
            top: "50%",
            width: isStreak ? p.size : p.size,
            height: isStreak ? p.size * 6 : p.size,
            marginLeft: -p.size / 2,
            marginTop: -p.size / 2,
            borderRadius: isStreak ? p.size : "50%",
            background: p.color,
            boxShadow: `0 0 ${p.size * 1.6}px ${p.color}`,
            opacity: 0,
            // Custom properties feed the shared keyframes below, so every
            // particle animates off one rule instead of N inline keyframes.
            "--pb-angle": `${p.angle}deg`,
            "--pb-distance": `${p.distance}px`,
            "--pb-spin": `${p.spin}deg`,
            animation: `particleBurstFly ${durationMs}ms cubic-bezier(0.12, 0.7, 0.25, 1) ${p.delayMs}ms both`,
          }}
        />
      ))}

      <style>{`
        @keyframes particleBurstFly {
          0% {
            opacity: 0;
            transform: rotate(var(--pb-angle)) translateY(0) rotate(calc(-1 * var(--pb-angle))) scale(0.3);
          }
          15% { opacity: 1; }
          70% { opacity: 1; }
          100% {
            opacity: 0;
            transform:
              rotate(var(--pb-angle))
              translateY(calc(-1 * var(--pb-distance)))
              rotate(calc(-1 * var(--pb-angle) + var(--pb-spin)))
              scale(0.55);
          }
        }
      `}</style>
    </div>
  );
};

export default ParticleBurst;
